import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import PageHead from "@/components/PageHead";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  Users,
  GraduationCap,
  UserCheck,
  TrendingUp,
  FileText,
  Activity,
  AlertTriangle,
  Sparkles,
  Download,
  Heart,
} from "lucide-react";
import {
  ResponsiveContainer,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RTooltip,
  LineChart,
  Line,
  CartesianGrid,
} from "recharts";

type Org = {
  id: string;
  name: string;
  plan: string;
  billing_status: string;
  student_license_limit: number | null;
};

type MemberRow = {
  user_id: string;
  member_role: string;
  status: string;
  created_at: string;
};

type StudentProfile = {
  id: string;
  full_name: string | null;
  email: string | null;
};

type AssessmentRow = {
  id: string;
  user_id: string;
  status: string;
  trait_scores: any;
  completed_at: string | null;
  started_at: string | null;
  created_at: string;
};

const HD_PILLARS = [
  { key: "purpose", label: "Purpose DNA™" },
  { key: "strength", label: "Strength DNA™" },
  { key: "learning", label: "Learning DNA™" },
  { key: "capability", label: "Capability DNA™" },
  { key: "opportunity", label: "Opportunity DNA™" },
  { key: "growth", label: "Growth DNA™" },
  { key: "contribution", label: "Contribution DNA™" },
];

const FUTURE_SKILLS = [
  "AI Readiness",
  "Communication",
  "Leadership",
  "Critical Thinking",
  "Collaboration",
  "Digital Literacy",
  "Entrepreneurship",
];

export default function InstitutionIntelligence() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [org, setOrg] = useState<Org | null>(null);
  const [members, setMembers] = useState<MemberRow[]>([]);
  const [students, setStudents] = useState<StudentProfile[]>([]);
  const [assessments, setAssessments] = useState<AssessmentRow[]>([]);
  const [goals, setGoals] = useState<any[]>([]);
  const [reflections, setReflections] = useState<any[]>([]);
  const [visionBoards, setVisionBoards] = useState<any[]>([]);
  const [parentGuideEntries, setParentGuideEntries] = useState<any[]>([]);
  const [reports, setReports] = useState<any[]>([]);
  const [activity, setActivity] = useState<any[]>([]);

  useEffect(() => {
    (async () => {
      if (!user) return;
      const { data: mem } = await supabase
        .from("organization_members")
        .select("organization_id, organizations(id,name,plan,billing_status,student_license_limit)")
        .eq("user_id", user.id)
        .eq("member_role", "institution_admin")
        .eq("status", "active")
        .limit(1)
        .maybeSingle();
      const o = (mem as any)?.organizations as Org | undefined;
      if (!o) {
        setLoading(false);
        return;
      }
      setOrg(o);

      const { data: allMembers } = await supabase
        .from("organization_members")
        .select("user_id, member_role, status, created_at")
        .eq("organization_id", o.id);
      setMembers((allMembers ?? []) as MemberRow[]);

      const studentIds = (allMembers ?? [])
        .filter((m: any) => m.member_role === "student" && m.status === "active")
        .map((m: any) => m.user_id);

      if (studentIds.length > 0) {
        const [{ data: profs }, { data: assess }, { data: gs }, { data: refs }, { data: vbs }, { data: reps }] = await Promise.all([
          supabase.from("profiles").select("id, full_name, email").in("id", studentIds),
          supabase.from("assessments").select("id, user_id, status, trait_scores, completed_at, started_at, created_at").in("user_id", studentIds),
          supabase.from("goals").select("id, user_id, status, created_at, updated_at").in("user_id", studentIds),
          supabase.from("monthly_reflections").select("id, user_id, month_index, created_at").in("user_id", studentIds),
          supabase.from("vision_boards").select("id, user_id, updated_at").in("user_id", studentIds),
          supabase.from("generated_reports").select("id, user_id, created_at").in("user_id", studentIds),
        ]);
        setStudents((profs ?? []) as StudentProfile[]);
        setAssessments((assess ?? []) as AssessmentRow[]);
        setGoals(gs ?? []);
        setReflections(refs ?? []);
        setVisionBoards(vbs ?? []);
        setReports(reps ?? []);

        const { data: pge } = await (supabase as any)
          .from("parent_guide_entries")
          .select("id, student_user_id, completed_at, created_at")
          .eq("organization_id", o.id);
        setParentGuideEntries(pge ?? []);
      }

      const { data: audit } = await (supabase as any)
        .from("institution_audit_log")
        .select("id, event, actor_email, target_email, created_at, metadata")
        .eq("organization_id", o.id)
        .order("created_at", { ascending: false })
        .limit(20);
      setActivity(audit ?? []);

      setLoading(false);
    })();
  }, [user]);

  const studentIds = useMemo(() => students.map((s) => s.id), [students]);
  const parentMembers = useMemo(() => members.filter((m) => m.member_role === "parent"), [members]);
  const teacherMembers = useMemo(
    () => members.filter((m) => m.member_role === "teacher" && m.status === "active"),
    [members],
  );

  // KPIs
  const kpis = useMemo(() => {
    const total = students.length;
    const completed = new Set(
      assessments.filter((a) => a.status === "completed" || a.status === "scored").map((a) => a.user_id),
    );
    const started = new Set(assessments.map((a) => a.user_id));
    const parentsInvited = parentMembers.length;
    const parentsConnected = parentMembers.filter((p) => p.status === "active").length;
    const reportsReady = new Set(reports.map((r) => r.user_id)).size;

    // Human Development Score (composite from completion + engagement)
    const engagement =
      total > 0
        ? (completed.size / total) * 40 +
          (parentsConnected / Math.max(total, 1)) * 20 +
          (new Set(goals.filter((g) => g.status === "completed").map((g) => g.user_id)).size / Math.max(total, 1)) * 20 +
          (new Set(reflections.map((r) => r.user_id)).size / Math.max(total, 1)) * 20
        : 0;
    const hdsScore = Math.round(Math.min(100, engagement));

    return {
      total,
      completedPct: total ? Math.round((completed.size / total) * 100) : 0,
      completedCount: completed.size,
      startedCount: started.size,
      reportsReady,
      parentsInvited,
      parentsConnected,
      parentPct: parentsInvited ? Math.round((parentsConnected / parentsInvited) * 100) : 0,
      hdsScore,
      teachers: teacherMembers.length,
    };
  }, [students, assessments, parentMembers, teacherMembers, reports, goals, reflections]);

  const institutionHealth = useMemo(() => {
    const score = kpis.hdsScore;
    let label = "At Risk";
    let color = "bg-red-500";
    if (score >= 80) {
      label = "Excellent";
      color = "bg-emerald-500";
    } else if (score >= 60) {
      label = "Healthy";
      color = "bg-green-500";
    } else if (score >= 40) {
      label = "Needs Attention";
      color = "bg-amber-500";
    }
    return { score, label, color };
  }, [kpis.hdsScore]);

  // Assessment status buckets
  const assessmentBuckets = useMemo(() => {
    const byUser = new Map<string, string>();
    for (const a of assessments) byUser.set(a.user_id, a.status);
    let completed = 0,
      pending = 0,
      notStarted = 0;
    for (const s of students) {
      const st = byUser.get(s.id);
      if (st === "completed" || st === "scored") completed++;
      else if (st === "in_progress") pending++;
      else notStarted++;
    }
    return [
      { name: "Completed", value: completed },
      { name: "In Progress", value: pending },
      { name: "Not Started", value: notStarted },
    ];
  }, [assessments, students]);

  // Completion trend last 6 months
  const completionTrend = useMemo(() => {
    const now = new Date();
    const buckets: { m: string; label: string; count: number }[] = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      buckets.push({ m, label: d.toLocaleString(undefined, { month: "short" }), count: 0 });
    }
    for (const a of assessments) {
      if (!a.completed_at) continue;
      const d = new Date(a.completed_at);
      const m = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const b = buckets.find((x) => x.m === m);
      if (b) b.count++;
    }
    return buckets;
  }, [assessments]);

  // Career intelligence (aggregate results)
  const careerIntel = useMemo(() => {
    const clusterCount = new Map<string, number>();
    const careerCount = new Map<string, number>();
    for (const a of assessments) {
      const r = a.trait_scores ?? {};
      const clusters = Array.isArray(r.clusters) ? r.clusters : [];
      for (const c of clusters.slice(0, 3)) {
        const name = c?.name ?? c?.title ?? (typeof c === "string" ? c : null);
        if (name) clusterCount.set(name, (clusterCount.get(name) ?? 0) + 1);
      }
      const careers = Array.isArray(r.careers) ? r.careers : [];
      for (const c of careers.slice(0, 3)) {
        const name = c?.title ?? c?.name ?? (typeof c === "string" ? c : null);
        if (name) careerCount.set(name, (careerCount.get(name) ?? 0) + 1);
      }
    }
    const toArr = (m: Map<string, number>) =>
      Array.from(m.entries())
        .sort((a, b) => b[1] - a[1])
        .slice(0, 6)
        .map(([name, count]) => ({ name, count }));
    return { clusters: toArr(clusterCount), careers: toArr(careerCount) };
  }, [assessments]);

  // Human development pillars (avg from results.humanDevelopment or fabricate proxy)
  const hdPillars = useMemo(() => {
    const sums: Record<string, { total: number; count: number }> = {};
    for (const p of HD_PILLARS) sums[p.key] = { total: 0, count: 0 };
    for (const a of assessments) {
      const hd = a.trait_scores?.humanDevelopment ?? a.trait_scores?.pillars ?? null;
      if (!hd) continue;
      for (const p of HD_PILLARS) {
        const v = Number(hd[p.key] ?? hd[p.label] ?? NaN);
        if (!isNaN(v)) {
          sums[p.key].total += v;
          sums[p.key].count += 1;
        }
      }
    }
    return HD_PILLARS.map((p) => ({
      pillar: p.label.replace(" DNA™", ""),
      score: sums[p.key].count ? Math.round((sums[p.key].total / sums[p.key].count) * 10) / 10 : 0,
    }));
  }, [assessments]);

  const futureSkills = useMemo(() => {
    const sums: Record<string, { total: number; count: number }> = {};
    for (const s of FUTURE_SKILLS) sums[s] = { total: 0, count: 0 };
    for (const a of assessments) {
      const fs = a.trait_scores?.futureSkills ?? {};
      for (const s of FUTURE_SKILLS) {
        const v = Number(fs[s] ?? fs[s.toLowerCase()] ?? NaN);
        if (!isNaN(v)) {
          sums[s].total += v;
          sums[s].count += 1;
        }
      }
    }
    return FUTURE_SKILLS.map((s) => ({
      skill: s,
      score: sums[s].count ? Math.round((sums[s].total / sums[s].count) * 10) / 10 : 0,
    }));
  }, [assessments]);

  // Students needing attention
  const needsAttention = useMemo(() => {
    const assessByUser = new Map(assessments.map((a) => [a.user_id, a] as const));
    const goalByUser = new Set(goals.map((g) => g.user_id));
    const reflByUser = new Set(reflections.map((r) => r.user_id));
    const vbByUser = new Set(visionBoards.map((v) => v.user_id));
    const list: { student: StudentProfile; reasons: string[] }[] = [];
    for (const s of students) {
      const reasons: string[] = [];
      const a = assessByUser.get(s.id);
      if (!a) reasons.push("Assessment not started");
      else if (a.status === "in_progress") reasons.push("Assessment incomplete");
      if (!goalByUser.has(s.id)) reasons.push("No goals set");
      if (!reflByUser.has(s.id)) reasons.push("No reflection submitted");
      if (!vbByUser.has(s.id)) reasons.push("No Vision Board");
      if (reasons.length > 0) list.push({ student: s, reasons });
    }
    return list.slice(0, 15);
  }, [students, assessments, goals, reflections, visionBoards]);

  // Parent engagement
  const parentEng = useMemo(() => {
    const completedRecs = parentGuideEntries.filter((e) => e.completed_at).length;
    return {
      invited: kpis.parentsInvited,
      connected: kpis.parentsConnected,
      consentPct: kpis.parentPct,
      guidesViewed: new Set(parentGuideEntries.map((e) => e.student_user_id)).size,
      activitiesCompleted: completedRecs,
    };
  }, [parentGuideEntries, kpis]);

  const exportCsv = () => {
    const rows = [
      ["timestamp", "event", "actor", "target"],
      ...activity.map((e) => [e.created_at, e.event, e.actor_email ?? "", e.target_email ?? ""]),
    ];
    const csv = rows
      .map((r) => r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `institution-activity-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="container py-10 text-muted-foreground">Loading intelligence…</div>;
  if (!org)
    return (
      <div className="container max-w-2xl py-16 text-center">
        <h1 className="text-3xl font-bold mb-3">No institution assigned</h1>
        <Link to="/dashboard"><Button>Go to Dashboard</Button></Link>
      </div>
    );

  return (
    <>
      <PageHead
        title={`${org.name} · Institution Intelligence™`}
        description="Executive decision support for school leadership."
        path="/institution/intelligence"
      />
      <div className="container max-w-7xl py-10 space-y-8">
        <header className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Sparkles className="w-6 h-6" /> Institution Intelligence™
            </h1>
            <p className="text-muted-foreground mt-1">{org.name} — how are our students developing?</p>
          </div>
          <div className="flex gap-2">
            <Link to="/institution"><Button variant="outline" size="sm">Dashboard</Button></Link>
            <Link to="/institution/audit"><Button variant="outline" size="sm">Audit Log</Button></Link>
            <Button size="sm" onClick={exportCsv}><Download className="w-4 h-4 mr-1.5" /> Export CSV</Button>
          </div>
        </header>

        {/* KPIs */}
        <section id="celebrations" className="grid grid-cols-2 md:grid-cols-4 gap-4 scroll-mt-24">
          <Kpi icon={<GraduationCap className="w-4 h-4" />} label="Total Students" value={kpis.total} />
          <Kpi icon={<TrendingUp className="w-4 h-4" />} label="Assessment Completion" value={`${kpis.completedPct}%`} hint={`${kpis.completedCount} of ${kpis.total}`} />
          <Kpi icon={<FileText className="w-4 h-4" />} label="Reports Ready" value={kpis.reportsReady} />
          <Kpi icon={<UserCheck className="w-4 h-4" />} label="Parent Engagement" value={`${kpis.parentPct}%`} hint={`${kpis.parentsConnected}/${kpis.parentsInvited}`} />
          <Kpi icon={<Heart className="w-4 h-4" />} label="Human Development Score" value={kpis.hdsScore} />
          <Kpi icon={<Users className="w-4 h-4" />} label="Active Teachers" value={kpis.teachers} />
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">
                <Activity className="w-4 h-4" /> Institution Health
              </div>
              <div className="flex items-center gap-2">
                <span className={`inline-block w-2.5 h-2.5 rounded-full ${institutionHealth.color}`} />
                <span className="text-xl font-semibold">{institutionHealth.label}</span>
              </div>
              <div className="text-xs text-muted-foreground mt-1">Score {institutionHealth.score}/100</div>
            </CardContent>
          </Card>
          <Kpi icon={<AlertTriangle className="w-4 h-4" />} label="Needing Attention" value={needsAttention.length} />
        </section>

        {/* Assessment progress + trend */}
        <section className="grid lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader><CardTitle>Assessment Progress</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {assessmentBuckets.map((b) => {
                  const pct = kpis.total ? Math.round((b.value / kpis.total) * 100) : 0;
                  return (
                    <div key={b.name}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{b.name}</span>
                        <span className="text-muted-foreground">{b.value} · {pct}%</span>
                      </div>
                      <Progress value={pct} className="h-2" />
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Completion Over Time</CardTitle></CardHeader>
            <CardContent>
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={completionTrend}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                    <XAxis dataKey="label" />
                    <YAxis allowDecimals={false} />
                    <RTooltip />
                    <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Career Intelligence */}
        <section className="grid lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader><CardTitle>Top Career Clusters</CardTitle></CardHeader>
            <CardContent>
              {careerIntel.clusters.length === 0 ? (
                <p className="text-sm text-muted-foreground">No cluster data yet.</p>
              ) : (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={careerIntel.clusters} layout="vertical" margin={{ left: 20 }}>
                      <XAxis type="number" allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={140} />
                      <RTooltip />
                      <Bar dataKey="count" fill="hsl(var(--primary))" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Top Career Interests</CardTitle></CardHeader>
            <CardContent>
              {careerIntel.careers.length === 0 ? (
                <p className="text-sm text-muted-foreground">No career interest data yet.</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {careerIntel.careers.map((c) => (
                    <li key={c.name} className="flex justify-between border-b pb-1.5">
                      <span>{c.name}</span>
                      <Badge variant="secondary">{c.count}</Badge>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </section>

        {/* Human Development */}
        <Card>
          <CardHeader>
            <CardTitle>Human Development Overview</CardTitle>
            <p className="text-sm text-muted-foreground">Institution-level averages across the seven DNA pillars.</p>
          </CardHeader>
          <CardContent>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={hdPillars}>
                  <PolarGrid />
                  <PolarAngleAxis dataKey="pillar" />
                  <PolarRadiusAxis angle={30} domain={[0, 100]} />
                  <Radar dataKey="score" stroke="hsl(var(--primary))" fill="hsl(var(--primary))" fillOpacity={0.35} />
                  <RTooltip />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Future Skills */}
        <Card>
          <CardHeader><CardTitle>Future Skills</CardTitle></CardHeader>
          <CardContent>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={futureSkills} margin={{ bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="skill" angle={-15} textAnchor="end" interval={0} height={60} />
                  <YAxis domain={[0, 100]} />
                  <RTooltip />
                  <Bar dataKey="score" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Parent + Teacher engagement */}
        <section className="grid lg:grid-cols-2 gap-4">
          <Card>
            <CardHeader><CardTitle>Parent Engagement</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <Metric label="Invited" value={parentEng.invited} />
              <Metric label="Connected" value={parentEng.connected} />
              <Metric label="Consent" value={`${parentEng.consentPct}%`} />
              <Metric label="Growth Guides viewed" value={parentEng.guidesViewed} />
              <Metric label="Activities completed" value={parentEng.activitiesCompleted} />
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle>Teacher Engagement</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-2 gap-4 text-sm">
              <Metric label="Active teachers" value={teacherMembers.length} />
              <Metric label="Assigned students" value={kpis.total} />
              <Metric label="Completion rate" value={`${kpis.completedPct}%`} />
              <Metric label="Reports available" value={kpis.reportsReady} />
            </CardContent>
          </Card>
        </section>

        {/* Needs attention */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Students Needing Attention
            </CardTitle>
          </CardHeader>
          <CardContent>
            {needsAttention.length === 0 ? (
              <p className="text-sm text-muted-foreground">Every student is engaged. Nice work.</p>
            ) : (
              <div className="divide-y">
                {needsAttention.map(({ student, reasons }) => (
                  <div key={student.id} className="py-3 flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-medium truncate">{student.full_name ?? student.email}</div>
                      <div className="flex flex-wrap gap-1.5 mt-1.5">
                        {reasons.map((r) => (
                          <Badge key={r} variant="outline" className="text-xs">{r}</Badge>
                        ))}
                      </div>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button size="sm" variant="outline" disabled title="Nudges are sent from the Teacher console — coming to this view soon">Send reminder</Button>
                      <Button size="sm" variant="outline" asChild>
                        <Link to="/teacher?tab=invite">Invite parent</Link>
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Activity feed */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Activity className="w-5 h-5" /> Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            {activity.length === 0 ? (
              <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
            ) : (
              <ul className="space-y-2 text-sm">
                {activity.map((e) => (
                  <li key={e.id} className="flex justify-between border-b pb-2">
                    <div>
                      <span className="font-medium">{e.event}</span>
                      {e.actor_email && <span className="text-muted-foreground"> · {e.actor_email}</span>}
                      {e.target_email && <span className="text-muted-foreground"> → {e.target_email}</span>}
                    </div>
                    <span className="text-xs text-muted-foreground">{new Date(e.created_at).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function Kpi({ icon, label, value, hint }: { icon: React.ReactNode; label: string; value: string | number; hint?: string }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center gap-2 text-muted-foreground text-sm mb-2">{icon}{label}</div>
        <div className="text-3xl font-bold">{value}</div>
        {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}

function Metric({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}
